/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
export default {
    baseStyle: (props) => ({
        swatchGroup: {
            flexDirection: 'column',
            display: 'grid'
        },
        swatchLabel: {
            marginBottom: 3,
            ...(props.isPDP && {
                fontSize: '12px',
                fontStyle: 'normal',
                fontWeight: 400,
                lineHeight: '15px'
            }),
            ...(props.displayLabelOnly && {
                fontSize: 'sm',
                fontWeight: 'bold'
            })
        },
        swatchDisplayName: {
            color: 'black',
            fontSize: '12px',
            fontStyle: 'normal',
            fontWeight: 400,
            lineHeight: '15px'
        },
        swatch: {
            position: 'relative',
            backgroundColor: 'white',
            _focus: {
                outline: 'none',
                boxShadow: 'outline'
            }
        },
        swatchesWrapper: {
            flexWrap: 'wrap',
            ...(props.isSizeSwatch &&
                props.isPDP && {
                    display: 'grid',
                    gridTemplateColumns: 'repeat(6, 1fr)',
                    gap: 1
                }),
            ...((props.giftCardType || props.giftCardValue || props.giftECard) && {
                display: 'grid',
                gap: '4px',
                gridTemplateColumns:
                    props.giftCardValue || props.giftECard
                        ? `repeat(${props.childrenCount || 5}, 1fr)`
                        : props.giftCardType
                        ? `repeat(${props.childrenCount || 4}, 1fr)`
                        : 'repeat(2, 1fr)'
            }),
            ...(props?.id === 'color' &&
                !props.giftCardType && {
                    columnGap: {base: '21.5px', lg: 2},
                    rowGap: {base: '21.5px', lg: 1}
                })
        },
        swatchButton: {
            borderColor: 'gray.450',
            _disabled: {
                opacity: 1
            }
        }
    }),

    variants: {
        circle: (props) => ({
            swatch: {
                height: props.isPDP
                    ? {base: props.selected ? '24px' : '24px', lg: 5}
                    : props.isQuickView
                    ? {base: 6, lg: 4}
                    : props.isProductTile
                    ? {base: '10px', lg: 5}
                    : 10,
                width: props.isPDP
                    ? {base: props.selected ? '24px' : '24px', lg: 5}
                    : props.isQuickView
                    ? {base: 6, lg: 4}
                    : props.isProductTile
                    ? {base: '10px', lg: 5}
                    : 10,
                borderRadius: 'full',
                cursor: 'pointer',
                minWidth: 'unset',
                padding: props.isPDP
                    ? '2px'
                    : props.isProductTile
                    ? {base: 0, lg: 0.5}
                    : props.isQuickView
                    ? {base: 0.5, lg: '1px'}
                    : 1,
                marginRight: 0,
                ...(props.isQuickView && {marginRight: {base: 0, lg: 1}}),
                marginLeft: props.isProductTile ? 1 : 0,
                marginBottom: 0,
                marginTop: props.isQuickView ? {base: 'unset', lg: '-6px'} : 'unset',
                color: props.selected ? 'black' : 'gray.450',
                border: props.isPDP ? '0' : '1px',
                borderColor: 'unset',
                _hover: {
                    borderColor: 'black',
                    borderWidth: !props.selected && props.isPDP ? 0 : 1,
                    borderStyle: 'solid'
                },
                _active: {
                    background: 'transparent'
                },
                ...(props.isPDP && {
                    backgroundColor: 'gray.450',
                }),
                ...(props.selected && {
                    border: '1px solid',
                    borderColor: 'black',
                    backgroundColor: 'white'
                }),
                swatchContent: {
                    ...(props.isPDP && {
                        _after: {
                            content: {base: '""', lg: 'unset'},
                            position: 'absolute',
                            width: '44px',
                            height: '44px',
                            backgroundColor: 'transparent',
                            left: '50%',
                            top: '50%',
                            transform: 'translate(-50%, -50%)'
                        },
                    }),
                    '@supports (-webkit-appearance: none) and (stroke-color: transparent)': {
                        padding: props.isPDP ? '2px'
                            : props.isProductTile ? {base: 0, lg: 0.5}
                            : props.isQuickView ? {base: 0.5, lg: '1px'} : 1,
                    },
                    '.is-mac-chrome &': {
                        padding: props.isPDP ? '2px'
                            : props.isProductTile ? {base: 0, lg: 0.5}
                            : props.isQuickView ? {base: 0.5, lg: '1px'} : 1,
                    },
                }
            },
            swatchButton: {
                position: 'relative',
                height: props.isPDP
                    ? '100%'
                    : props.isQuickView
                    ? '100%'
                    : props.isProductTile
                    ? {base: '100%', lg: 4}
                    : 7,
                width: props.isPDP
                    ? '100%'
                    : props.isQuickView
                    ? '100%'
                    : props.isProductTile
                    ? {base: '100%', lg: 4}
                    : 7,
                borderColor: 'gray.750',
                overflow: 'hidden',
                borderRadius: 'full',
                opacity: 1,
                minWidth: props.isProductTile ? {base: 'unset', lg: 4} : 'auto',
                _focus: {
                    outline: 'none'
                }
            }
        }),
        square: (props) => ({
            swatch: {
                height: props.isQuickView ? {base: 10, lg: 8} : 10,
                width: props.isQuickView ? '100%' : 'max-content',
                minWidth: props.isQuickView ? '48px' : '40px',
                borderRadius: 0,
                marginRight: props.isQuickView ? {base: 2, lg: 1} : 2,
                marginBottom: props.isQuickView ? 0 : 2,
                paddingLeft: props.isQuickView ? 1 : 3,
                paddingRight: props.isQuickView ? 1 : 3,
                borderColor: props.selected ? 'black' : 'gray.450',
                borderStyle: 'solid',
                borderWidth: 1,
                backgroundColor: props.selected ? 'black' : 'white',
                color: props.selected ? 'white' : 'black',
                fontSize: 12,
                fontStyle: 'normal',
                fontWeight: 400,
                lineHeight: '15px',
                _focus: {
                    outline: 'none'
                },
                _hover: {
                    textDecoration: 'none',
                    borderColor: 'gray.900',
                    ...(!props.disabled && {
                        color: 'white',
                        background: 'black'
                    })
                },
                _active: {
                    borderColor: 'gray.900'
                },
                ...(props.isPDP && {
                    borderColor: 'gray.50',
                    width: 'max-content',
                    minWidth: '100%',
                    margin: 0,
                    padding: 0
                }),
                swatchContent: {
                     ...(props.disabled || props.id === 'length') && {
                        '& .swatch-content': {
                            ...(props.disabled && {
                                color: props?.selected ? 'white' : 'grey.600',
                                position: 'relative',
                                _after: {
                                    content: '""',
                                    display: 'block',
                                    position: 'absolute',
                                    height: '1px',
                                    width: 'calc(100% + 8px)',
                                    top: '50%',
                                    left: '50%',
                                    background: 'currentColor',
                                    transform: 'translate(-50%, -50%)',
                                    marginTop: '0.1em',
                                    '@supports (-webkit-appearance: none) and (stroke-color: transparent)': {
                                        marginTop: '-0.15em',
                                    },
                                    '.is-mac-chrome &': {
                                        marginTop: '0.04em',
                                    },
                                    ...(props.id === 'length' && {
                                        marginTop: '0.2em',
                                    }),
                                }
                            }),
                            ...(props.id === 'length' && {
                                textTransform: 'capitalize'
                            })
                        }
                    }
                }
            }
        }),
        giftCard: (props) => ({
            swatch: {
                borderRadius: 5,
                overflow: 'hidden',
                aspectRatio: '87.23 / 54.91',
                borderColor: 'gray.900',
                border: props.selected ? '1.5px solid' : 0,
                padding: `${props.selected ? '2px' : '0'} !important`,
                height: 'auto',
                width: '100%',
                // Reset line-height and flex-direction from outline button variant
                lineHeight: 'normal !important',
                flexDirection: 'row !important',
                paddingTop: `${props.selected ? '2px' : '0'} !important`,
                paddingBottom: `${props.selected ? '2px' : '0'} !important`
            },
            swatchButton: {
                height: '100%',
                width: '100%',
                minWidth: 'unset'
            }
        })
    },

    parts: ['swatch', 'swatchButton', 'swatchContent']
}
